import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import adminApi from "./adminApi";
import {
  Upload, FileSpreadsheet, Download, Search, RotateCcw, Loader2, CheckCircle2,
  AlertTriangle, X, ChevronDown, Info,
} from "lucide-react";

const MAX_MB = 10;

const thisYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => thisYear + 1 - i);

// What the Excel must look like (shown in the guide on this page)
const SHEET_GUIDE = [
  {
    sheet: "DM M.CH",
    starts: "DM",
    headings: ["Sl.No.", "NEET Roll.No. *", "NEET Rank", "Name of Student (Dr.) *", "Department"],
    saved: "neet_roll_no, neet_rank, student_name, department_name",
  },
  {
    sheet: "PG MD MS",
    starts: "PG",
    headings: ["SL.No.", "Exam Roll no. *", "All India Rank", "Name of Student *", "Course"],
    saved: "exam_roll_no, all_india_rank, student_name, course",
  },
  {
    sheet: "MBBS",
    starts: "MBBS",
    headings: ["Sl.No.", "NEET Roll No. *", "Name of the Students *", "Category", "Gender", "Remarks"],
    saved: "neet_roll_no, student_name, category, gender, remarks",
  },
  {
    sheet: "DMLT & DMRT",
    starts: "DMLT",
    headings: ["Sl.No.", "Application Number *", "Applicant Name *", "Type", "CMR-UR", "Admitted Course", "Admitted Cat"],
    saved: "application_number, student_name, type, cmr_ur, admitted_course, admitted_cat",
  },
  {
    sheet: "BMLT, BMRT, COA, COSA, COTT",
    starts: "BMLT",
    headings: ["Sl.No.", "Application Number *", "Applicant Name *", "Type", "CMR-UR", "Admitted Course", "Admitted Cat"],
    saved: "application_number, student_name, type, cmr_ur, admitted_course, admitted_cat",
  },
];

// Tabs: All + the five sheets of the Excel (value = what the API expects)
const PROGRAMMES = [
  { value: "", label: "All" },
  { value: "DM_MCH", label: "DM M.CH" },
  { value: "PG", label: "PG MD MS" },
  { value: "MBBS", label: "MBBS" },
  { value: "DMLT_DMRT", label: "DMLT & DMRT" },
  { value: "ALLIED", label: "BMLT, BMRT, COA, COSA, COTT" },
];

// Table columns for each programme (the same columns that programme's Excel sheet has)
const C = {
  year: { h: "Year", cell: (r) => r.admission_year },
  name: { h: "Student Name", strong: true, cell: (r) => r.student_name },
  gender: { h: "Gender", cell: (r) => r.gender },
  neetRoll: { h: "NEET Roll No.", mono: true, cell: (r) => r.neet_roll_no },
  examRoll: { h: "Exam Roll No.", mono: true, cell: (r) => r.exam_roll_no },
  appNo: { h: "Application No.", mono: true, cell: (r) => r.application_number },
  neetRank: { h: "NEET Rank", cell: (r) => r.neet_rank },
  air: { h: "All India Rank", cell: (r) => r.all_india_rank },
  dept: { h: "Department", cell: (r) => r.department_name },
  course: { h: "Course", cell: (r) => r.course },
  category: { h: "Category", cell: (r) => r.category },
  remarks: { h: "Remarks", cell: (r) => r.remarks },
  type: { h: "Type", cell: (r) => r.type },
  cmr: { h: "CMR-UR", cell: (r) => r.cmr_ur },
  admCourse: { h: "Admitted Course", cell: (r) => r.admitted_course },
  admCat: { h: "Admitted Cat", cell: (r) => r.admitted_cat },
  programme: { h: "Sheet", cell: (r) => r.programme },
  // "All" tab: one column that shows whichever number / rank / course the student has
  anyNo: { h: "Roll / Application No", mono: true, cell: (r) => r.neet_roll_no || r.exam_roll_no || r.application_number },
  anyRank: { h: "Rank", cell: (r) => r.neet_rank || r.all_india_rank || r.cmr_ur },
  anyCourse: { h: "Department / Course", cell: (r) => r.department_name || r.course || r.admitted_course },
  anyCat: { h: "Category", cell: (r) => r.category || r.admitted_cat },
};
const COLUMNS = {
  "": [C.year, C.programme, C.name, C.gender, C.anyNo, C.anyRank, C.anyCourse, C.anyCat, C.type, C.remarks],
  DM_MCH: [C.year, C.neetRoll, C.name, C.neetRank, C.dept],
  PG: [C.year, C.examRoll, C.name, C.air, C.course],
  MBBS: [C.year, C.neetRoll, C.name, C.gender, C.category, C.remarks],
  DMLT_DMRT: [C.year, C.appNo, C.name, C.type, C.cmr, C.admCourse, C.admCat],
  ALLIED: [C.year, C.appNo, C.name, C.type, C.cmr, C.admCourse, C.admCat],
};

const inputCls =
  "w-full border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition";

const fmtSize = (bytes) =>
  bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

export default function ManageAdmissionList() {
  // ── Upload ─────────────────────────────────────────────────────────────
  const [year, setYear] = useState("");
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [checking, setChecking] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState(null); // result of the "Check file" step
  const [uploadError, setUploadError] = useState("");
  const fileRef = useRef(null);

  // ── Guide / toast ──────────────────────────────────────────────────────
  const [guideOpen, setGuideOpen] = useState(true);
  const autoCollapsed = useRef(false);
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3200);
  };

  // ── List ───────────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [years, setYears] = useState([]);
  const [groups, setGroups] = useState([]); // programme counts for the buttons
  const [allTotal, setAllTotal] = useState(0);
  const [group, setGroup] = useState(""); // selected tab ("" = All)
  const [filterYear, setFilterYear] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false); // after the first load, refreshes never blank the table
  const [listError, setListError] = useState("");

  const reqId = useRef(0);
  const fetchList = async (o = {}) => {
    const myReq = ++reqId.current;
    setLoading(true);
    setListError("");
    try {
      const res = await adminApi.get("/admin/admission-list", {
        params: {
          year: o.year ?? filterYear,
          q: o.q ?? searchQ,
          group: o.group ?? group,
        },
      });
      // First visit: show the latest admission year (re-load once with it)
      if (o.firstLoad && !o.year && (res.data.years || []).length > 0) {
        const latest = String(res.data.years[0].admission_year);
        setFilterYear(latest);
        return await fetchList({ year: latest });
      }
      if (myReq !== reqId.current) return; // a newer search already started
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
      setGroups(res.data.groups || []);
      setAllTotal(res.data.allTotal || 0);
      setYears(res.data.years || []);
      // Once there is data, tuck the how-to away (still one click to open)
      if (!autoCollapsed.current && (res.data.years || []).length > 0) {
        autoCollapsed.current = true;
        setGuideOpen(false);
      }
    } catch (err) {
      setListError(err.response?.data?.message || "Could not load the admission list.");
    } finally {
      if (myReq === reqId.current) {
        setLoading(false);
        setLoadedOnce(true);
      }
    }
  };

  useEffect(() => {
    fetchList({ firstLoad: true });
  }, []);

  // Search as you type: the list updates on every character ("ad" -> Adani, Aditya, ...)
  const typedOnce = useRef(false);
  useEffect(() => {
    if (!typedOnce.current) { typedOnce.current = true; return; }
    fetchList({ q: searchQ }); // every character; older responses are ignored (see reqId)
  }, [searchQ]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchList();
  };

  // Click a sheet: show only that sheet's students
  const pickGroup = (g) => {
    setGroup(g);
    fetchList({ group: g });
  };

  const handleReset = () => {
    setFilterYear("");
    setSearchQ("");
    fetchList({ year: "", q: "" });
  };

  // ── File handling ──────────────────────────────────────────────────────
  const chooseFile = (f) => {
    setUploadError("");
    setPreview(null);
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) {
      setFile(null);
      setUploadError("Only .xlsx files are allowed. Open an .xls file in Excel and save it as .xlsx first.");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      setFile(null);
      setUploadError(`The file is larger than ${MAX_MB} MB.`);
      return;
    }
    setFile(f);
    // The Excel has no year column — suggest one from the file name ("Admission List - 2025.xlsx")
    const m = f.name.match(/(20\d{2})/);
    if (m && !year && YEAR_OPTIONS.includes(Number(m[1]))) setYear(m[1]);
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setUploadError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const buildForm = (dryRun) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("year", year);
    if (dryRun) fd.append("dryRun", "1");
    return fd;
  };

  const canSubmit = file && year && !checking && !importing;

  // Step 1: read the file and show what WOULD happen (nothing is saved)
  const handleCheck = async () => {
    setUploadError("");
    setPreview(null);
    setChecking(true);
    try {
      const res = await adminApi.post("/admin/admission-list/import", buildForm(true));
      setPreview(res.data);
    } catch (err) {
      setUploadError(err.response?.data?.message || "Could not check the file. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  // Step 2: save
  const handleImport = async () => {
    setUploadError("");
    setImporting(true);
    try {
      const res = await adminApi.post("/admin/admission-list/import", buildForm(false));
      const { inserted, updated } = res.data;
      showToast("success", `Saved — ${inserted} new, ${updated} updated.`);
      const y = String(res.data.year);
      clearFile();
      setFilterYear(y);
      setSearchQ("");
      fetchList({ year: y, q: "" });
    } catch (err) {
      setUploadError(err.response?.data?.message || "Import failed. Nothing was saved.");
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await adminApi.get("/admin/admission-list/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "admission-list-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast("error", "Could not download the template.");
    }
  };

  const cols = COLUMNS[group];
  const groupLabel = PROGRAMMES.find((p) => p.value === group)?.label;
  const changes = preview ? preview.inserted + preview.updated : 0;
  const sheetsFound = preview ? preview.sheets.reduce((n, s) => n + s.rows, 0) : 0;

  return (
    <div className="h-full overflow-y-auto pr-1 space-y-4">
      {/* Title */}
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
          Manage Admission List
        </h3>
        <button
          type="button"
          onClick={downloadTemplate}
          className="flex items-center gap-1.5 bg-white border border-slate-200 hover:border-[#0EA5A4] text-[#0B2545] text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
        >
          <Download size={15} />
          Download Excel template
        </button>
      </div>

      {/* ── Upload card ───────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <h4 className="text-sm font-semibold text-[#0B2545] mb-3">Upload admission list (Excel)</h4>

        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-4">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              Admission year <span className="text-red-500">*</span>
            </label>
            <select
              className={inputCls}
              value={year}
              onChange={(e) => { setYear(e.target.value); setPreview(null); }}
            >
              <option value="">-- Select --</option>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <p className="text-[11px] text-slate-400 mt-1.5 leading-snug">
              The Excel has no year column, so choose the batch it belongs to.
            </p>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-500 mb-1 block">
              Excel file (.xlsx, max {MAX_MB} MB) <span className="text-red-500">*</span>
            </label>

            {file ? (
              <div className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2.5 bg-slate-50">
                <FileSpreadsheet size={22} className="text-[#0EA5A4] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800 font-medium truncate">{file.name}</div>
                  <div className="text-xs text-slate-400">{fmtSize(file.size)}</div>
                </div>
                <button
                  type="button"
                  onClick={clearFile}
                  disabled={checking || importing}
                  title="Remove file"
                  className="text-slate-400 hover:text-red-600 transition disabled:opacity-40"
                >
                  <X size={17} />
                </button>
              </div>
            ) : (
              <label
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  chooseFile(e.dataTransfer.files?.[0]);
                }}
                className={`flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg px-4 py-6 cursor-pointer transition ${
                  dragOver ? "border-[#0EA5A4] bg-[#0EA5A4]/5" : "border-slate-200 hover:border-[#0EA5A4]"
                }`}
              >
                <Upload size={22} className="text-[#0EA5A4]" />
                <span className="text-sm text-slate-600">
                  Drag the Excel file here or <span className="text-[#0EA5A4] font-medium">browse</span>
                </span>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={(e) => chooseFile(e.target.files?.[0])}
                />
              </label>
            )}
          </div>
        </div>

        {uploadError && (
          <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            {uploadError}
          </p>
        )}

        {!preview && (
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={handleCheck}
              disabled={!canSubmit}
              className="flex items-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white text-[13px] font-medium rounded-lg px-5 py-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {checking ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
              {checking ? "Checking..." : "Check file"}
            </button>
            <span className="text-xs text-slate-400">
              Checking only shows what will happen — nothing is saved until you confirm.
            </span>
          </div>
        )}

        {/* Result of "Check file" */}
        <AnimatePresence>
          {preview && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-4 border border-slate-200 rounded-lg overflow-hidden"
            >
              <div className="px-4 py-2.5 bg-[#0B2545]/5 border-b border-slate-200 text-sm font-medium text-[#0B2545]">
                Result of checking for admission year {preview.year}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50">
                      <th className="px-4 py-2 font-semibold">Sheet</th>
                      <th className="px-3 py-2 font-semibold text-right">Students in Excel</th>
                      <th className="px-3 py-2 font-semibold text-right">New</th>
                      <th className="px-3 py-2 font-semibold text-right">Changed</th>
                      <th className="px-3 py-2 font-semibold text-right">Already same</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sheets.map((s) => {
                      const g = preview.byGroup[s.group] || { rows: 0, inserted: 0, updated: 0, unchanged: 0 };
                      return (
                        <tr key={s.sheet} className="border-t border-slate-100">
                          <td className="px-4 py-2 text-slate-700">{s.sheet}</td>
                          <td className="px-3 py-2 text-right text-slate-700">{g.rows}</td>
                          <td className="px-3 py-2 text-right text-emerald-700 font-medium">{g.inserted}</td>
                          <td className="px-3 py-2 text-right text-amber-700 font-medium">{g.updated}</td>
                          <td className="px-3 py-2 text-right text-slate-400">{g.unchanged}</td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                      <td className="px-4 py-2 text-[#0B2545]">Total</td>
                      <td className="px-3 py-2 text-right">{sheetsFound}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{preview.inserted}</td>
                      <td className="px-3 py-2 text-right text-amber-700">{preview.updated}</td>
                      <td className="px-3 py-2 text-right text-slate-500">{preview.unchanged}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {preview.missingFromFile > 0 && (
                <p className="px-4 py-2 text-xs text-slate-500 border-t border-slate-100 flex items-start gap-2">
                  <Info size={14} className="shrink-0 mt-0.5" />
                  {preview.missingFromFile} student(s) saved earlier for {preview.year} are not in this file. They are
                  kept as they are (nothing is deleted).
                </p>
              )}

              {preview.warnings.length > 0 && (
                <div className="px-4 py-2.5 text-xs bg-amber-50 border-t border-amber-100 text-amber-800">
                  <div className="font-semibold mb-1 flex items-center gap-1.5">
                    <AlertTriangle size={14} /> {preview.warnings.length} row(s) / sheet(s) need attention
                  </div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {preview.warnings.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                  {preview.warnings.length > 8 && (
                    <div className="mt-1">…and {preview.warnings.length - 8} more.</div>
                  )}
                </div>
              )}

              <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center gap-3">
                {sheetsFound === 0 ? (
                  <span className="text-sm text-red-600">No students were found in this file. Check the format guide below.</span>
                ) : changes === 0 ? (
                  <span className="text-sm text-slate-600 flex items-center gap-1.5">
                    <CheckCircle2 size={16} className="text-emerald-600" />
                    Nothing to save — the database already matches this file.
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={handleImport}
                    disabled={importing}
                    className="flex items-center gap-2 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-semibold rounded-lg px-5 py-2 transition disabled:opacity-60"
                  >
                    {importing ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                    {importing ? "Saving..." : `Save ${preview.inserted} new` + (preview.updated ? ` & update ${preview.updated}` : "")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setPreview(null)}
                  disabled={importing}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                >
                  Back
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Saved students ────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-[#0B2545]">Saved students</h4>

        <form
          onSubmit={handleSearch}
          className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex flex-wrap items-end gap-3"
        >
          <div className="min-w-[130px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Admission year</label>
            <select value={filterYear} onChange={(e) => setFilterYear(e.target.value)} className={inputCls}>
              <option value="">All years</option>
              {years.map((y) => (
                <option key={y.admission_year} value={y.admission_year}>
                  {y.admission_year} ({y.total})
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-slate-500 mb-1 block">Name, roll / application no, course</label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" />
              <input
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder="Type a name, e.g. ad..."
                className="w-full border border-slate-200 rounded-lg pl-7 pr-7 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
              />
              {loading && loadedOnce && (
                <Loader2 size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 animate-spin text-[#0EA5A4]" />
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="submit"
              className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
            >
              <Search size={13} />
              Search
            </button>
            <button
              type="button"
              onClick={handleReset}
              title="Reset filters"
              className="flex items-center text-slate-400 hover:text-slate-700 px-1.5 py-1.5 rounded-lg transition"
            >
              <RotateCcw size={13} />
            </button>
          </div>
        </form>

        {/* Tabs: All / sheets, with the number of students in brackets */}
        <div className="flex flex-wrap items-center gap-2">
          {PROGRAMMES.map((p) => {
            const active = group === p.value;
            const count = p.value === "" ? allTotal : groups.find((g) => g.value === p.value)?.total ?? 0;
            return (
              <button
                key={p.value || "all"}
                type="button"
                onClick={() => pickGroup(p.value)}
                className={`px-3.5 py-1.5 rounded-lg text-[13px] font-medium border transition ${
                  active
                    ? "bg-[#0EA5A4] border-[#0EA5A4] text-white shadow-sm"
                    : "bg-white border-slate-200 text-[#0B2545] hover:border-[#0EA5A4]"
                }`}
              >
                {p.label} ({count})
              </button>
            );
          })}
        </div>

        <div>
          {/* Students of the selected sheet */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading && !loadedOnce ? (
            <div className="flex items-center justify-center py-14 text-slate-400 text-sm gap-2">
              <Loader2 size={18} className="animate-spin" /> Loading...
            </div>
          ) : listError ? (
            <div className="py-14 text-center text-sm text-red-500">{listError}</div>
          ) : rows.length === 0 ? (
            <div className="py-14 text-center">
              <FileSpreadsheet size={28} className="mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-400">
                {years.length === 0 ? "No admission list uploaded yet." : group ? `No ${groupLabel} students match your filters.` : "No students match your filters."}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-left text-sm">
                      {["Sl No", ...cols.map((c) => c.h)].map((h, i, a) => (
                        <th
                          key={h}
                          className={`th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 whitespace-nowrap ${i < a.length - 1 ? "border-r" : ""}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.id} className="hover:bg-[#0EA5A4]/10 transition-colors border-b border-slate-200">
                        <td className="px-3 py-2 text-slate-400 border-r border-slate-200">{i + 1}</td>
                        {cols.map((c, ci) => (
                          <td
                            key={c.h}
                            className={`px-3 py-2 ${ci < cols.length - 1 ? "border-r border-slate-200" : ""} ${
                              c.strong ? "text-slate-800 font-medium" : c.mono ? "font-mono text-xs text-slate-700" : "text-slate-500"
                            }`}
                          >
                            {dash(c.cell(r))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="px-4 py-2 border-t border-slate-200 text-xs text-slate-500">
                {total} student{total === 1 ? "" : "s"}{group ? ` in ${groupLabel}` : ""}
              </div>
            </>
          )}
          </div>
        </div>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
